import { useCallback, useMemo } from "react";
import styled from "styled-components";
import NexaTypography from "../../../../shared/NexaTypography";
import PlusIcon from "../../../../../customIcons/Plus";
import MinusIcon from "../../../../../customIcons/Minus";
import Button from "../../../../shared/Button";
import ChildAgeSelect from "./ChildAgeSelect";
import {
  MAX_CHILD_AGE,
  MAX_NUMBER_OF_ADULTS,
  MAX_NUMBER_OF_CHILDREN,
  MIN_CHILD_AGE,
  MIN_NUMBER_OF_ADULTS,
  MIN_NUMBER_OF_CHILDREN,
} from "../../../../../constants/hotelConstants";
import { useTranslation } from "../../../../../i18n";
import { dispatchCustomEvent } from "../../../../../services/widgetEventsService";
import { WidgetGATriggerEvents } from "../../../../../enums/WidgetEvents";
import { getRoomChildAgeElementId } from "../../utils";

interface Props {
  areChildrenAgeTouched: boolean;
  onChange: (value: { adults: number; childrenAge: (number | null)[] }) => void;
  roomIndex: number;
  values: { adults: number; childrenAge: (number | null)[] };
}

const RoomField = ({ areChildrenAgeTouched, onChange, roomIndex, values }: Props) => {
  const { t } = useTranslation();

  // Adults
  const disabledMaxAdults = useMemo(() => values.adults >= MAX_NUMBER_OF_ADULTS, [values.adults]);
  const disabledMinAdults = useMemo(() => values.adults <= MIN_NUMBER_OF_ADULTS, [values.adults]);
  const incrementNumberOfAdults = useCallback(() => {
    dispatchCustomEvent({ widgetEvent: WidgetGATriggerEvents.HotelsExtendedSearchAdultAdded });
    onChange({ ...values, adults: values.adults + 1 });
  }, [onChange, values]);
  const decrementNumberOfAdults = useCallback(() => {
    dispatchCustomEvent({ widgetEvent: WidgetGATriggerEvents.HotelsExtendedSearchAdultRemoved });
    onChange({ ...values, adults: values.adults - 1 });
  }, [onChange, values]);

  // Children
  const disabledMaxChildren = useMemo(
    () => values.childrenAge.length >= MAX_NUMBER_OF_CHILDREN,
    [values.childrenAge]
  );
  const disabledMinChildren = useMemo(
    () => values.childrenAge.length <= MIN_NUMBER_OF_CHILDREN,
    [values.childrenAge]
  );
  const incrementNumberOfChildren = useCallback(() => {
    dispatchCustomEvent({ widgetEvent: WidgetGATriggerEvents.HotelsExtendedSearchChildAdded });
    onChange({ ...values, childrenAge: [...values.childrenAge, null] });
  }, [onChange, values]);
  const decrementNumberOfChildren = useCallback(() => {
    dispatchCustomEvent({ widgetEvent: WidgetGATriggerEvents.HotelsExtendedSearchChildRemoved });
    onChange({ ...values, childrenAge: values.childrenAge.slice(0, -1) });
  }, [onChange, values]);
  const changeChildAge = useCallback(
    (index: number) => (updatedAge: number | null) => {
      dispatchCustomEvent({
        widgetEvent: WidgetGATriggerEvents.HotelsExtendedSearchChildAgeSelected,
        details: { age: updatedAge },
      });
      onChange({
        ...values,
        childrenAge: values.childrenAge.map((age, childIndex) =>
          index === childIndex ? updatedAge : age
        ),
      });
    },
    [onChange, values]
  );

  const hasChildError = useCallback(
    (childAge: number | null) => {
      return areChildrenAgeTouched && childAge === null;
    },
    [areChildrenAgeTouched]
  );

  return (
    <div>
      <Row>
        <TitleBox>
          <Text isHeavy>{t("tix_search_form_adults")}</Text>
          <Text>{`${t("tix_search_form_age")} ${MAX_CHILD_AGE + 1}+`}</Text>
        </TitleBox>
        <ButtonsContainer>
          <IconButton
            bordered
            color="white"
            disabled={disabledMinAdults}
            onlyIcon
            onClick={decrementNumberOfAdults}
          >
            <MinusIcon />
          </IconButton>
          <Value>{values.adults.toString()}</Value>
          <IconButton
            bordered
            color="white"
            disabled={disabledMaxAdults}
            onlyIcon
            onClick={incrementNumberOfAdults}
          >
            <PlusIcon />
          </IconButton>
        </ButtonsContainer>
      </Row>
      <ChildrenRow>
        <TitleBox>
          <Text isHeavy>{t("tix_search_form_children")}</Text>
          <Text>{`${t("tix_search_form_age")} ${MIN_CHILD_AGE}-${MAX_CHILD_AGE}`}</Text>
        </TitleBox>
        <ButtonsContainer>
          <IconButton
            bordered
            color="white"
            disabled={disabledMinChildren}
            onlyIcon
            onClick={decrementNumberOfChildren}
          >
            <MinusIcon />
          </IconButton>
          <Value>{values.childrenAge.length.toString()}</Value>
          <IconButton
            bordered
            color="white"
            disabled={disabledMaxChildren}
            onlyIcon
            onClick={incrementNumberOfChildren}
          >
            <PlusIcon />
          </IconButton>
        </ButtonsContainer>
      </ChildrenRow>
      {values.childrenAge.length > 0 &&
        values.childrenAge.map((childAge, index) => {
          const hasError = hasChildError(childAge);
          return (
            <ChildAgeSelectContainer
              hasError={hasError}
              key={index}
              id={getRoomChildAgeElementId(roomIndex, index)}
            >
              <ChildAgeSelect
                childIndex={index + 1}
                hasError={hasError}
                onChange={changeChildAge(index)}
                value={childAge}
              />
            </ChildAgeSelectContainer>
          );
        })}
    </div>
  );
};

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  white-space: nowrap;

  &:not(:last-child) {
    margin-bottom: 20px;
  }
`;

const ChildrenRow = styled(Row)`
  &:not(:last-child) {
    margin-bottom: 12px;
  }
`;

const ButtonsContainer = styled.div`
  display: flex;
  align-items: center;
`;

const IconButton = styled(Button)`
  height: 25px;
  width: 25px;
  padding: 0;

  svg {
    width: 11px;
  }
`;

const TitleBox = styled.div`
  display: flex;
  flex-direction: column;
`;

const Text = styled(NexaTypography)`
  line-height: 15px;
`;

const Value = styled(NexaTypography)`
  display: inline-flex;
  justify-content: center;
  width: 26px;
`;

const ChildAgeSelectContainer = styled.div<{ hasError: boolean }>`
  &:not(:last-child) {
    margin-bottom: ${({ hasError }) => (hasError ? 0 : "10px")};
  }
`;

export default RoomField;
